import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchBookingConsole,
    addShopType,
    updateShopType,
    deleteShopType,
    addResource,
    updateResource,
    deleteResource,
    createBooking,
    updateBooking,
    updateBookingStatus,
    rescheduleBooking,
    clearBookingError,
    clearBookingNotice,
} from "../../Store/Slices/Tenant/Booking";
import { isActiveBooking } from "./bookingService";
import { getShopCtx, setShopCtx, ShopSwitcher } from "./components/shop/ShopBits";
import BookingShell from "./components/BookingShell";
import ShopOverview from "./components/shop/ShopOverview";
import CalendarTab from "./components/shop/CalendarTab";
import ShopBookings from "./components/shop/ShopBookings";
import CustomersTab from "./components/shop/CustomersTab";
import CalendarsTab from "./components/shop/CalendarsTab";
import PortalTab from "./components/shop/PortalTab";
import WidgetTab from "./components/shop/WidgetTab";

const META = {
    overview: { title: "Overview", subtitle: "Today, pending requests and plan usage" },
    calendar: { title: "Calendar", subtitle: "Day, week and month across your resources" },
    bookings: { title: "Bookings", subtitle: "Confirm, edit, move and cancel customer bookings" },
    customers: { title: "Customers", subtitle: "Directory built from your bookings" },
    calendars: { title: "Resources", subtitle: "Doctors, rooms, calendars and slot settings" },
    portal: { title: "Client portal", subtitle: "Exactly what your customers see" },
    widget: { title: "Website widget", subtitle: "Attach booking to your own website" },
};

/**
 * Shop booking console in the Nekobiz workspace layout.
 * Each shop manages its own calendars, resources, customers and bookings.
 *
 * Props (both optional, used by the admin console to impersonate a shop):
 *   impersonate = shop object to lock the console to (hides the switcher)
 *   onExit      = called when the admin exits impersonation
 *
 * Exported both as named (`Booking`) and default export.
 */
export function Booking({ impersonate, onExit } = {}) {
    const dispatch = useDispatch();
    const { plans, shops, resourceTypes, resources, bookings, loading, error, notice } = useSelector(
        (state) => state.booking
    );

    const [shopId, setShopId] = useState((impersonate && impersonate.id) || "");
    const [tab, setTab] = useState("overview");

    useEffect(() => {
        dispatch(fetchBookingConsole());
    }, [dispatch]);

    useEffect(() => {
        if (impersonate && impersonate.id) setShopId(impersonate.id);
    }, [impersonate]);

    useEffect(() => {
        if (!impersonate && !shopId && shops.length) setShopId(getShopCtx(shops));
    }, [shops, shopId, impersonate]);

    const shop = shops.find((s) => s.id === shopId) || null;

    const pickShop = (id) => {
        setShopId(id);
        setShopCtx(id);
    };


    const myBookings = useMemo(
        () => bookings.filter((b) => b.ownerId === shopId),
        [bookings, shopId]
    );
    const pendingCount = useMemo(
        () => myBookings.filter((b) => b.status === "pending").length,
        [myBookings]
    );
    const customerCount = useMemo(() => {
        const keys = new Set(
            myBookings.map((b) => `${(b.customer || "").trim().toLowerCase()}|${(b.email || "").trim().toLowerCase()}`)
        );
        return keys.size;
    }, [myBookings]);

    if (loading && shops.length === 0) {
        return (
            <div className="hk-page">
                <div className="hk-empty">
                    <div className="hk-empty-icon">🏪</div>
                    <div className="font-bold text-zinc-700">Loading shop console…</div>
                </div>
            </div>
        );
    }

    if (!shop) {
        return (
            <div className="hk-page">
                <div className="hk-card">
                    <h5 className="hk-card-title">No shops yet</h5>
                    <p className="text-sm text-[#64748b] mb-0">
                        No shops exist yet. Ask the app owner to onboard your shop first.
                    </p>
                </div>
            </div>
        );
    }

    const sections = [
        {
            label: "Workspace",
            items: [
                { id: "overview", icon: "⌂", label: "Overview" },
                { id: "calendar", icon: "▦", label: "Calendar" },
                { id: "bookings", icon: "◫", label: "Bookings", badge: pendingCount },
                { id: "customers", icon: "◎", label: "Customers", badge: customerCount },
                { id: "calendars", icon: "▣", label: "Resources" },
            ],
        },
        {
            label: "Client",
            items: [
                { id: "portal", icon: "🌐", label: "Client portal" },
                { id: "widget", icon: "🔌", label: "Website widget" },
            ],
        },
    ];

    const meta = META[tab] || META.overview;
    const shared = { shop, plans, resourceTypes, resources, bookings };

    return (
        <div className="booking-full">
            {((impersonate && onExit) || notice || error) && (
                <div className="max-w-[1400px] mx-auto w-full px-3 sm:px-6 pt-3 space-y-3">
                    {(impersonate && onExit) && (
                        <div className="hk-card flex flex-wrap justify-between items-center gap-2" style={{ borderColor: "#fde68a", background: "#fffbeb" }}>
                            <span className="text-sm">
                                🕶️ <strong>Impersonating {shop.business}</strong>
                                <span className="text-[#92400e]"> — everything you do here applies to this shop.</span>
                            </span>
                            <button className="hk-btn hk-btn-dark hk-btn-sm" onClick={onExit}>
                                ← Back to admin
                            </button>
                        </div>
                    )}
                    {notice && (
                        <div className="alert alert-success alert-dismissible mb-0">
                            {notice}
                            <button type="button" className="btn-close" onClick={() => dispatch(clearBookingNotice())}></button>
                        </div>
                    )}
                    {error && (
                        <div className="alert alert-danger alert-dismissible mb-0">
                            {error}
                            <button type="button" className="btn-close" onClick={() => dispatch(clearBookingError())}></button>
                        </div>
                    )}
                </div>
            )}

            <BookingShell
                brand={shop.business}
                sub={`${shop.plan === "pro" ? "Pro" : "Free"} plan · ${shop.status}`}
                sections={sections}
                active={tab}
                onNav={setTab}
                title={meta.title}
                subtitle={meta.subtitle}
                actions={impersonate ? null : <ShopSwitcher shops={shops} value={shop.id} onChange={pickShop} />}
                foot={
                    <div className="px-2 py-1 text-[11px] text-[#64748b]">
                        {myBookings.filter(isActiveBooking).length} active bookings · {shop.plan === "pro" ? "Pro" : "Free"} plan
                    </div>
                }
            >
                {tab === "overview" && (
                    <ShopOverview
                        {...shared}
                        onGoBookings={() => setTab("bookings")}
                        onGoCalendars={() => setTab("calendars")}
                    />
                )}
                {tab === "calendar" && (
                    <CalendarTab
                        {...shared}
                        onStatus={(id, status) => dispatch(updateBookingStatus({ id, status }))}
                        onEdit={(id, patch) => dispatch(updateBooking({ id, patch }))}
                        onReschedule={(id, slot) => dispatch(rescheduleBooking({ id, slot }))}
                        onBook={(payload) => dispatch(createBooking(payload))}
                    />
                )}
                {tab === "bookings" && (
                    <ShopBookings
                        {...shared}
                        onStatus={(id, status) => dispatch(updateBookingStatus({ id, status }))}
                        onReschedule={(id, slot) => dispatch(rescheduleBooking({ id, slot }))}
                        onEdit={(id, patch) => dispatch(updateBooking({ id, patch }))}
                    />
                )}
                {tab === "customers" && (
                    <CustomersTab
                        {...shared}
                        onStatus={(id, status) => dispatch(updateBookingStatus({ id, status }))}
                        onEdit={(id, patch) => dispatch(updateBooking({ id, patch }))}
                    />
                )}
                {tab === "calendars" && (
                    <CalendarsTab
                        {...shared}
                        onAddType={(payload) => dispatch(addShopType({ shopId: shop.id, payload }))}
                        onUpdateType={(id, patch) => dispatch(updateShopType({ id, patch }))}
                        onDeleteType={(id) => dispatch(deleteShopType(id))}
                        onAddResource={(typeId, name) => dispatch(addResource({ shopId: shop.id, typeId, name }))}
                        onRenameResource={(id, name) => dispatch(updateResource({ id, patch: { name } }))}
                        onDeleteResource={(id) => dispatch(deleteResource(id))}
                    />
                )}
                {tab === "portal" && (
                    <PortalTab
                        {...shared}
                        onBook={(payload) => dispatch(createBooking(payload))}
                        onReschedule={(id, slot) => dispatch(rescheduleBooking({ id, slot }))}
                        onCancel={(id) => dispatch(updateBookingStatus({ id, status: "cancelled" }))}
                    />
                )}
                {tab === "widget" && (
                    <WidgetTab
                        {...shared}
                        onBook={(payload) => dispatch(createBooking(payload))}
                        onReschedule={(id, slot) => dispatch(rescheduleBooking({ id, slot }))}
                        onCancel={(id) => dispatch(updateBookingStatus({ id, status: "cancelled" }))}
                    />
                )}
            </BookingShell>
        </div>
    );
}

export default Booking;
